<template>
  <header class="app-header">
    <h1>Cartographers</h1>
    <!-- Nothing in here is usable from behind the gate, and a stale
         saved game in localStorage was enough to show a locked-out
         visitor a Leave button. The title stays; the controls go. -->
    <div v-if="isApproved" class="app-header__right">
      <AdminPanel v-if="isAdmin" />
      <button
        class="icon-button"
        title="How to play"
        aria-label="How to play"
        @click="showHowTo = true"
      >?</button>
      <span v-if="roomMeta?.roomCode && !inLobby" class="header-code">{{ roomMeta.roomCode }}</span>
      <button v-if="inGame" class="icon-button" title="Leave" aria-label="Leave" @click="confirmLeave">✕</button>
    </div>
  </header>

  <main class="app-main">
    <SiteGate v-if="!isApproved" />
    <GameTable v-else-if="inGame" @leave="leave" />
    <RoomLobby v-else-if="roomId || wantsOnline" @back="wantsOnline = false" />
    <div v-else class="screen">
      <h2>Cartographers</h2>
      <p class="muted">
        The map-drawing game. Everyone charts the same wilderness at once,
        shaping forests, villages, farms and seas to the queen's decrees —
        while monsters creep in from your neighbours' pens.
      </p>

      <div class="field">
        <label>Robots at the table</label>
        <div class="segmented">
          <button
            v-for="n in 5"
            :key="n"
            :class="{ 'is-selected': n === opponents }"
            @click="opponents = n"
          >{{ n }}</button>
        </div>
      </div>
      <button class="primary" @click="newGame">Play against the robots</button>
      <button v-if="hasSavedGame" class="secondary" @click="resume">Resume the saved game</button>
      <button class="secondary" @click="wantsOnline = true">Start or join an online game</button>

      <MyStatsPanel />
      <LeaderBoard />

      <!-- The way home. Same mark and wording in every game; the icon is the
           hub's own ring-of-seats logo. -->
      <a class="hub-link" href="https://aroundtableround.com/" title="All the games">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.4" opacity="0.5" />
          <circle cx="20" cy="12" r="1.7" fill="currentColor" /><circle cx="17.66" cy="17.66" r="1.7" fill="currentColor" />
          <circle cx="12" cy="20" r="1.7" fill="currentColor" /><circle cx="6.34" cy="17.66" r="1.7" fill="currentColor" />
          <circle cx="4" cy="12" r="1.7" fill="currentColor" /><circle cx="6.34" cy="6.34" r="1.7" fill="currentColor" />
          <circle cx="12" cy="4" r="1.7" fill="currentColor" /><circle cx="17.66" cy="6.34" r="1.7" fill="currentColor" />
        </svg>
        Around Table Round
      </a>

      <!-- The install offer lives on the menu and nowhere else (Matt's call,
           house-wide). -->
      <InstallPrompt />
    </div>
  </main>

  <HowToPlay v-if="showHowTo" @close="dismissHowTo" />

  <!--
    Tap the stamp for what the screen actually measures. Three rounds of
    "there is dead space at the bottom" were guesswork because nobody could
    read the numbers off the phone in the room. Now anyone can.
  -->
  <!-- Tap reloads the app (an installed PWA has no reload button); press
       and hold is the viewport measurement, which is a debugging tool and
       not what a tap should do. -->
  <p
    class="build-stamp"
    role="button"
    tabindex="0"
    title="Tap to reload · hold to measure"
    @click="stampTap"
    @pointerdown="stampHold"
    @pointerup="stampRelease"
    @pointerleave="stampRelease"
    @pointercancel="stampRelease"
  >{{ measured || (refreshing ? 'reloading…' : stamp) }}</p>
  <BugReportButton v-if="firebaseConfigured" />
  <BugResolutionNotice v-if="firebaseConfigured" />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import GameTable from '@/components/game/GameTable.vue';
import RoomLobby from '@/components/lobby/RoomLobby.vue';
import SiteGate from '@/components/auth/SiteGate.vue';
import AdminPanel from '@/components/auth/AdminPanel.vue';
import InstallPrompt from '@/components/shared/InstallPrompt.vue';
import HowToPlay from '@/components/shared/HowToPlay.vue';
import BugReportButton from '@/components/shared/BugReportButton.vue';
import BugResolutionNotice from '@/components/shared/BugResolutionNotice.vue';
import MyStatsPanel from '@/components/shared/MyStatsPanel.vue';
import LeaderBoard from '@/components/shared/LeaderBoard.vue';
import { buildStamp, forceRefresh } from '@/utils/buildStamp';
import { viewportLine } from '@/utils/viewport';
import { initUpdateCheck } from '@/utils/checkForUpdate';
import { registerBugUi } from '@/utils/bugContext';

const HOWTO_SEEN_KEY = 'cartographers:howto-seen';

export default {
  name: 'App',
  components: {
    GameTable, RoomLobby, SiteGate, AdminPanel, InstallPrompt, BugReportButton, BugResolutionNotice, HowToPlay,
    MyStatsPanel, LeaderBoard,
  },
  data() {
    return {
      stamp: buildStamp(),
      measured: '',
      refreshing: false,
      held: false,
      wantsOnline: false,
      pendingJoinCode: null,
      showHowTo: false,
      opponents: 2,
    };
  },
  computed: {
    ...mapState(['roomId', 'roomMeta', 'firebaseConfigured', 'hasSavedGame', 'settings']),
    ...mapGetters(['isApproved', 'isAdmin', 'view', 'inLobby']),
    inGame() { return Boolean(this.view); },
  },
  watch: {
    isApproved(ok) { if (ok) this.consumeJoinCode(); },
  },
  created() {
    const code = new URLSearchParams(window.location.search).get('join');
    if (code) { this.pendingJoinCode = code; this.wantsOnline = true; }
    this.opponents = this.settings?.opponents || 2;
  },
  mounted() {
    // Menu-level UI the store doesn't hold, for bug reports.
    registerBugUi('app', () => ({
      wantsOnline: this.wantsOnline,
      showHowTo: this.showHowTo,
      pendingJoinCode: this.pendingJoinCode,
      menuOpponents: this.opponents,
    }));
    this.$store.dispatch('initAuth');
    initUpdateCheck(this.$store);
    if (this.isApproved) this.consumeJoinCode();

    // First-ever visit: open the walkthrough unprompted - but not over a
    // saved game someone is coming back to.
    if (!localStorage.getItem(HOWTO_SEEN_KEY) && !this.hasSavedGame) this.showHowTo = true;
  },
  methods: {
    toggleMeasure() {
      this.measured = this.measured ? '' : viewportLine();
    },
    // Hold for the measurement; a tap is a reload.
    stampHold() {
      this.held = false;
      this.holdTimer = setTimeout(() => { this.held = true; this.toggleMeasure(); }, 550);
    },
    stampRelease() {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = null;
    },
    stampTap() {
      if (this.held) { this.held = false; return; }
      if (this.measured) { this.measured = ''; return; }
      this.refreshing = true;
      forceRefresh();
    },
    dismissHowTo() {
      this.showHowTo = false;
      try { localStorage.setItem(HOWTO_SEEN_KEY, '1'); } catch { /* storage off */ }
    },
    consumeJoinCode() {
      if (!this.pendingJoinCode) return;
      const code = this.pendingJoinCode;
      this.pendingJoinCode = null;
      this.$store.dispatch('joinRoomByCode', code);
    },
    newGame() {
      this.$store.dispatch('newLocalGame', { opponents: this.opponents });
    },
    resume() { this.$store.dispatch('resumeGame'); },
    leave() {
      if (this.roomId) this.$store.dispatch('leaveRoom');
      else this.$store.dispatch('quitLocalGame');
      this.wantsOnline = false;
    },
    confirmLeave() {
      // eslint-disable-next-line no-alert -- one deliberate destructive confirm
      if (window.confirm('Leave this game?')) this.leave();
    },
  },
};
</script>
